import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Addon } from '@raptor-fe/core';
import { ServiceUtils } from '../service-utils/service-utils';

/**
 * API service for reading the add-on catalog. Add-ons are reusable bundles of items linked to a part
 * via Part.addonId; the quote flow fetches one by id to auto-expand its items into child quote lines.
 * The add-on controller is tenant + branch scoped, so both segments are supplied to the URL builder.
 */
@Injectable({
    providedIn: 'root',
})
export class AddonApiService {
    private readonly http = inject(HttpClient);
    private readonly serviceUtils = inject(ServiceUtils);

    /**
     * Retrieves a single add-on and its item composition by id.
     * GET {base}/addon/{addonId}
     *
     * The id is the trimmed TM SUB and can contain spaces or URL-reserved characters (e.g. "OIL SVC"), so it is
     * percent-encoded before interpolation to avoid a malformed or misrouted request, matching sibling api-services.
     */
    getAddonById(tenantId: string, branchId: string, addonId: string): Observable<Addon> {
        return this.http.get<Addon>(`${this.serviceUtils.getBaseUrl('addon', tenantId, branchId)}/${encodeURIComponent(addonId)}`);
    }

    /**
     * Retrieves every add-on for the tenant with its item composition, used by the add-on catalog list.
     * GET {base}/addon
     */
    getAddons(tenantId: string, branchId: string): Observable<Addon[]> {
        return this.http.get<Addon[]>(this.serviceUtils.getBaseUrl('addon', tenantId, branchId));
    }

    /**
     * Creates a new add-on with its items in one aggregate write.
     * POST {base}/addon
     */
    create(tenantId: string, branchId: string, addon: Addon): Observable<Addon> {
        return this.http.post<Addon>(this.serviceUtils.getBaseUrl('addon', tenantId, branchId), addon);
    }

    /**
     * Updates an add-on and its whole item composition in one aggregate write. The id is carried in the
     * body (the add-on itself), matching the controller's PUT contract.
     * PUT {base}/addon
     */
    update(tenantId: string, branchId: string, addon: Addon): Observable<Addon> {
        return this.http.put<Addon>(this.serviceUtils.getBaseUrl('addon', tenantId, branchId), addon);
    }

    /**
     * Deletes a whole add-on (header and all its items) by id.
     * DELETE {base}/addon/{addonId}
     */
    delete(tenantId: string, branchId: string, addonId: string): Observable<void> {
        return this.http.delete<void>(`${this.serviceUtils.getBaseUrl('addon', tenantId, branchId)}/${encodeURIComponent(addonId)}`);
    }
}
